import { useCallback, useEffect, useRef, useState } from 'react';
import {
  copyTextToClipboard,
  readTextFromClipboard,
} from '../../pages/qc/shared/utils';
import { computeTranscriptChangePercentage } from './utils/qcAssessmentQuestionUtils';
import { segmentsToPlainText } from './utils/transcriptInlineChipUtils';

/**
 * Clipboard text that differs from this task's vendor transcript by more than
 * this (character change %) is probably another task's transcript — a copy that
 * failed silently leaves the previous task's text on the clipboard.
 */
export const OTHER_TASK_CHANGE_PCT = 70;

const MESSAGE_MS = 4000;

const normalize = (s) => String(s ?? '').replace(/\s+/g, ' ').trim();

/**
 * Decide what the Paste button should do. Paste replaces the whole updated
 * transcript (it used to insert at the caret, which doubled the text when the
 * box already held it), so confirm before overwriting different text or
 * pasting something that doesn't look like this task's transcript.
 *
 * @returns {{ action: 'none' | 'replace' | 'confirm', reasons: string[] }}
 */
export function decidePaste({ clipboardText, currentText, vendorText }) {
  const clip = normalize(clipboardText);
  if (!clip) return { action: 'none', reasons: ['empty'] };
  const current = normalize(currentText);
  if (clip === current) return { action: 'none', reasons: ['same'] };

  const reasons = [];
  const vendor = normalize(vendorText);
  if (
    vendor &&
    computeTranscriptChangePercentage(vendor, clip) > OTHER_TASK_CHANGE_PCT
  ) {
    reasons.push('other-task');
  }
  if (current) reasons.push('overwrite');
  return { action: reasons.length ? 'confirm' : 'replace', reasons };
}

/** Explanation lines for the paste confirmation dialog. */
export function pasteConfirmLines(reasons) {
  const lines = [];
  if (reasons.includes('other-task')) {
    lines.push(
      "The clipboard text looks very different from this task's vendor transcript — it may be from another task.",
    );
  }
  if (reasons.includes('overwrite')) {
    lines.push('Pasting replaces the updated transcript you have now.');
  }
  return lines;
}

/**
 * Copy-vendor-transcript and paste-into-updated-transcript actions for the QC
 * assessment layouts, with visible feedback for both.
 */
export function useTranscriptClipboard({
  vendorText,
  readOnly,
  transcriptInputRef,
  transcriptSegments,
  onTranscriptEditorCommit,
}) {
  /** @type {[{ severity: 'success' | 'error' | 'info', text: string } | null, Function]} */
  const [message, setMessage] = useState(null);
  const timerRef = useRef(null);

  const show = useCallback((severity, text) => {
    setMessage({ severity, text });
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setMessage(null), MESSAGE_MS);
  }, []);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  const handleCopyVendorTranscript = useCallback(async () => {
    const ok = await copyTextToClipboard(String(vendorText ?? ''));
    if (ok) show('success', 'Vendor transcript copied.');
    else {
      show(
        'error',
        'Copy failed — the clipboard still has whatever was copied before. Select the vendor transcript and copy it manually.',
      );
    }
  }, [vendorText, show]);

  /** Paste waiting on the confirmation dialog: { text, reasons } */
  const [pendingPaste, setPendingPaste] = useState(null);

  const applyPaste = useCallback(
    (text) => {
      const api = transcriptInputRef?.current;
      if (!api?.replaceAllPlainText) return;
      // No refocus here: the editor skips syncing its DOM from props while it
      // has focus, and focusing before React re-renders would leave the old text.
      api.replaceAllPlainText(text.replace(/\r\n?/g, '\n'));
      onTranscriptEditorCommit?.();
      show('success', 'Updated transcript replaced with the clipboard text.');
    },
    [transcriptInputRef, onTranscriptEditorCommit, show],
  );

  const handlePasteIntoUpdatedTranscript = useCallback(async () => {
    if (readOnly) return;
    if (!transcriptInputRef?.current?.replaceAllPlainText) return;
    const text = await readTextFromClipboard();
    const currentText = segmentsToPlainText(transcriptSegments, {
      wrapTagsInBrackets: false,
    });
    const { action, reasons } = decidePaste({
      clipboardText: text,
      currentText,
      vendorText,
    });
    if (action === 'none') {
      if (reasons.includes('empty')) {
        show('error', 'Nothing to paste — the clipboard is empty or the browser blocked access.');
      } else {
        show('info', 'The updated transcript already matches the clipboard.');
      }
      return;
    }
    if (action === 'confirm') {
      setPendingPaste({ text, reasons });
      return;
    }
    applyPaste(text);
  }, [readOnly, transcriptInputRef, transcriptSegments, vendorText, show, applyPaste]);

  const confirmPendingPaste = useCallback(() => {
    if (pendingPaste) applyPaste(pendingPaste.text);
    setPendingPaste(null);
  }, [pendingPaste, applyPaste]);

  const cancelPendingPaste = useCallback(() => setPendingPaste(null), []);

  return {
    clipboardMessage: message,
    handleCopyVendorTranscript,
    handlePasteIntoUpdatedTranscript,
    /** Reasons the pending paste needs confirming, or null when no dialog is open. */
    pasteConfirmReasons: pendingPaste?.reasons ?? null,
    confirmPendingPaste,
    cancelPendingPaste,
  };
}
